import React, { useRef, useState } from "react";
import Button from "../../atomic/Button";
import { MdOutlineEdit } from "react-icons/md";
import { LiaExchangeAltSolid } from "react-icons/lia";
import { useDocument } from "./DocumentComponentContext";
import { useDocumentApiService } from "./ApiCalls/DocumentComponentApiService";
import { notify } from "../../../shared/context/AlertContext";

const ImageOverlayToolbar = () => {
  const [showEditImage, setShowEditImage] = React.useState(false);
  const fileInputRef: any = useRef(null);
  const { updateDocument, document } = useDocument();
  const {
    uploadDocument,
    updateKYCDocument,
    updateCoApplicantKYCDocument,
    updateAdditionalKYCDocumentMedia,
  } = useDocumentApiService();
  let [mediaID, setMediaID] = useState(null);

  const [file, setFile] = useState<{
    preview: string;
    raw: null | Blob;
    type: string;
  }>({ preview: "", raw: null, type: "" });

  function onEditImage() {
    updateDocument({ showEditDocument: true });
    setShowEditImage(!showEditImage);
  }

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.currentTarget.files?.[0];
    if (selectedFile) {
      let preview = URL.createObjectURL(selectedFile);
      setFile({
        preview: preview,
        raw: selectedFile ?? null,
        type: selectedFile.type,
      });
      const response: any = await uploadDocument({
        file: selectedFile,
        userID: document.loanDetails?.userID,
        mediaType: "Photo",
        pageName: document.pageName,
        token: document.token,
        isLenderPlatform: document.isLenderDashboard || false,
      });
      if (response?.data?.mediaID) {
        setMediaID(response?.data?.mediaID);
        setShowEditImage(!showEditImage);
        updateDocument({ showEditDocument: false, uploadedFile: preview });
      }
    }
  };

  function handleImageChange() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  const handleSaveImage = async () => {
    if (document.instance) {
      const base64Image = document.instance.toDataURL({ format: "png" });
      const base64Response = await fetch(base64Image);
      const imageBlob = await base64Response.blob();
      const mediaType = document.documentType;

      setFile({
        preview: base64Image,
        raw: imageBlob,
        type: "png",
      });

      const response: any = await uploadDocument({
        file: imageBlob,
        userID: document.loanDetails?.userID,
        mediaType: mediaType || "Photo",
        pageName: document.pageName,
        token: document.token,
        isLenderPlatform: document.isLenderDashboard || false,
      });
      if (response?.data?.mediaID) {
        mediaID = response?.data?.mediaID;
        setMediaID(response?.data?.mediaID);
        setShowEditImage(!showEditImage);
        updateDocument({ showEditDocument: false, uploadedFile: preview });
      }
    }

    if (!mediaID) {
      return;
    }

    let response = null;

    try {
      if (document.isAdditionalDocs) {
        response = await updateAdditionalKYCDocumentMedia({
          documentID: document.documentID,
          loanApplicationID: document.loanID,
          mediaIDs: [mediaID],
          token: document.token,
          pageName: document.pageName,
        });
      } else if (document.isCoApplicant) {
        response = await updateCoApplicantKYCDocument({
          loanApplicationID: document.loanID,
          userID: document.loanDetails?.userID ?? "",
          coApplicantKYCDetailsID: document.coApplicantKYCDetailsID,
          isBack: document.isBackImage || false,
          mediaID,
          token: document.token,
          pageName: document.pageName,
        });
      } else {
        response = await updateKYCDocument({
          changes: ["IMAGE UPDATED"],
          isBack: false,
          loanApplicationID: document.loanID,
          loanKYCDetailsID: document.loanKycDetailsID,
          mediaID,
          token: document.token,
          pageName: document.pageName,
          isLenderDashboard: document.isLenderDashboard || false,
        });
      }

      if (response?.status) {
        setFile({ preview: "", raw: null, type: "" });
      } else {
        throw new Error(response?.error || "Unknown error occurred");
      }
    } catch (error: any) {
      notify({
        title: "Error",
        text: error.message || "Unexpected error occurred",
      });
    }
  };

  return (
    <>
      <div className="z-40 absolute w-full">
        {showEditImage && (
          <div className="flex gap-2 justify-end">
            <Button
              id="cancelDocument"
              color="default"
              outline
              onClick={() => {
                updateDocument({ showEditDocument: false, uploadedFile: "" });
                setShowEditImage(false);
                setFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              id="saveDocument"
              color="primary"
              onClick={() => {
                handleSaveImage();
              }}
            >
              Save Image
            </Button>
          </div>
        )}

        {!showEditImage && (
          <div className="flex gap-2 justify-end">
            <Button
              id="editImage"
              color="default"
              outline
              onClick={() => {
                onEditImage();
              }}
            >
              <div className="flex gap-2 items-center">
                <MdOutlineEdit /> Edit Image
              </div>
            </Button>

            <Button
              id="changeImage"
              color="default"
              outline
              onClick={() => {
                handleImageChange();
              }}
            >
              <div className="flex gap-2 items-center">
                <LiaExchangeAltSolid /> Change Image
              </div>
            </Button>
          </div>
        )}

        <input
          onChange={onFileSelect}
          className="hidden"
          type="file"
          ref={fileInputRef}
          accept="image/jpeg,image/png,application/pdf"
          multiple={false}
        />
      </div>
    </>
  );
};
export default ImageOverlayToolbar;
